import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import InformationNotification from "@MasterBluePrint/Icons/InformationNotification";
import { getIncoTermUrlSetting } from "@MasterBluePrint/Store/Context/ContextSelector";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const shipToState = getShipToState(state, cartState.value?.shipToId);
    return {
        cart: cartState.value,
        pageTitle: getCurrentPage(state).fields.title,
        stCountry: shipToState.value?.country?.abbreviation,
        incoTermUrl: getIncoTermUrlSetting(state),
        siteName: state.context.website.name,
    };
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps>;

export interface CheckoutReviewAndSubmitHeaderStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    placeOrderButton?: ButtonPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
    incoTermGridItem?: GridItemProps;
    incoTermWrapper?: InjectableCss;
    incoTermIconWrapper?: InjectableCss;
    incoMessageWrapper?: GridItemProps;
    incoMessageWrapperRed?: GridItemProps;
    incoTermUrlMessage?: TypographyPresentationProps;
    incoTermUrlMessageRed?: TypographyPresentationProps;
    incoTermLinkTypography?: TypographyPresentationProps;
    incoTermLinkTypographyRed?: TypographyPresentationProps;
    crsHeaderHazmatMessageGridItem?: GridItemProps;
    crsHeaderHazmatMessage?: TypographyPresentationProps;
    crsHeaderShipFreightGridItem?: GridItemProps;
    crsHeaderShipFreightMessage?: TypographyPresentationProps;
}

export const checkoutReviewAndSubmitHeaderStyles: CheckoutReviewAndSubmitHeaderStyles = {
    gridItem: { width: 12 },
    headingText: {
        variant: "h2",
        css: css`
            margin-top: 2.1875rem;
            margin-bottom: 0;
            color: ${themeColors.colorPrimary};
            font-family: "Roboto", sans-serif;
            font-size: ${themeFontSizes.size22};
            font-weight: ${themeFontWeights.fontWeightMedium};
            line-height: 1.5625rem;
            position: relative;
            margin-left: 0;

            &:after {
                height: 0.188rem;
                width: 2.5rem;
                background-color: ${themeColors.colorDanger};
                content: "";
                display: inline-block;
                position: absolute;
                bottom: -0.4rem;
                left: 0.0625rem;
            }

            @media screen and (max-width: 990px) {
                margin-left: 0;

                &:after {
                    height: 0.18750625rem;
                    width: 1.25rem;
                    background-color: ${themeColors.colorDanger};
                    content: "";
                    display: inline-block;
                    position: absolute;
                    bottom: -0.31250625rem;
                    left: 0.0625rem;
                }
            }
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        position: fixed;
                        left: 0;
                        bottom: 0;
                        width: 100%;
                        background: ${getColor("common.background")({ theme })};
                        justify-content: center;
                        z-index: ${get(theme, "zIndex.stickyFooter")};
                    `,
                    css`
                        margin-left: 1rem;
                    `,
                    css`
                        margin-left: auto;
                    `,
                    css`
                        margin-left: auto;
                    `,
                    css`
                        margin-left: auto;
                    `,
                ])}
        `,
    },
    backButton: {
        variant: "secondary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 50%;
                        `,
                        css`
                            flex-shrink: 1;
                        `,
                        css`
                            flex-shrink: 1;
                        `,
                        css`
                            flex-shrink: 1;
                        `,
                        css`
                            flex-shrink: 1;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    placeOrderButton: {
        css: css`
            flex-shrink: 0;
            margin-left: 0.625rem;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 50%;
                        `,
                        css`
                            width: unset;
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    submitForApprovalButton: {
        css: css`
            flex-shrink: 0;
            margin-left: 0.625rem;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 50%;
                        `,
                        css`
                            width: unset;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    incoTermGridItem: {
        width: 12,
    },
    incoTermWrapper: {
        css: css`
            width:100%;
            display:flex;
            flex-flow:row;
            align-items:start;
            font-family: 'Lato', sans-serif;
            margin:0;
            border:1px solid rgb(230, 228, 191);
            border-left:3px solid #ed8c07;
            border-radius:3px;
            padding:10px 11px 11px;
            background: rgb(245, 244, 229);
            color: rgb(72, 66, 51);
        `,
    },
    incoTermIconWrapper: {
        css: css`
            flex:1 auto;
            margin:0 6px -14px 4px;
        `,
    },
    incoMessageWrapper: {
        css: css`
            flex:1 100%;
            line-height:1.2;
            margin:0 0 7px;
            :last-of-type {
                margin-bottom:0;
            }
        `,
    },
    incoMessageWrapperRed: {
        css: css`
            flex:1 100%;
            line-height:1.2;
            margin:6px 0 8px 15px;
        `,
    },
    incoTermUrlMessageRed: {
        css: css`
            color:#F00;
        `,
    },
    incoTermLinkTypography: {
        css: css`
            text-decoration: underline;
            :hover {
                text-decoration:none;
            }
        `,
    },
    incoTermLinkTypographyRed: {
        css: css`
            color:#F00;
            text-decoration: underline;
        `,
    },
    crsHeaderHazmatMessageGridItem: {
        width: 12,
        css: css`
            padding:5px 0 0;
        `,
    },
    crsHeaderHazmatMessage: {
        css: css`
            width:auto;
            background: #f9eae9;
            border:1px solid #df9088;
            border-left:3px solid #c63527;
            margin:0 40px 5px 15px;
            p {
                color: #222;
                font-size:90%;
                line-height:1.35;
                letter-spacing:.01rem;
                padding:3px 6px
            }
            
        `,
    },
    crsHeaderShipFreightGridItem: {
        width: 12,
        css: css`
            padding:5px 0 0;
        `,
    },
    crsHeaderShipFreightMessage: {
        css: css`
            width:auto;
            background: #e5f5fc;
            border:1px solid #72c8ed;
            border-left:3px solid #2060ad;
            margin:0 40px 5px 15px;
            p {
                color: #222;
                font-size:90%;
                line-height:1.35;
                letter-spacing:.01rem;
                padding:3px 6px
            }
            
        `,
    },
};

export const header = checkoutReviewAndSubmitHeaderStyles;
const styles = checkoutReviewAndSubmitHeaderStyles;

const CheckoutReviewAndSubmitHeader: FC<Props> = ({ pageTitle, cart, stCountry, incoTermUrl, siteName }) => {
    const featureFlags: string[] = ["TEUS-8689", "TEUS-8628"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const ldIncoVisibility = flagValues["TEUS-8689"];
    const ldDryiceMessageVisibility = flagValues["TEUS-8628"];
    const dryIceMessage = incoTermUrl?.dryIceMessage;
    const hasMD = cart?.cartLines?.some(({ properties }) =>
        properties?.shippingClassification?.trim().toUpperCase() === "MD"
    ) ?? false;
    const hasMG = cart?.cartLines?.some(({ properties }) =>
        properties?.shippingClassification?.trim().toUpperCase() === "MG"
    ) ?? false;  
    const hasML = cart?.cartLines?.some(({ properties }) =>
        properties?.shippingClassification?.trim().toUpperCase() === "ML"
    ) ?? false;        
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.gridItem}>
                <Typography {...styles.headingText} as="h1">
                    {pageTitle}
                </Typography>
            </GridItem>
            {((ldIncoVisibility && (cart && stCountry !== "" && stCountry !== "US" && siteName !== "TestEquity UK"))
                || (ldDryiceMessageVisibility && dryIceMessage && hasMD)) && (
                    <GridItem {...styles.incoTermGridItem}>
                        <StyledWrapper {...styles.incoTermWrapper}>
                            <StyledWrapper {...styles.incoTermIconWrapper}>
                                <Icon
                                    src={InformationNotification}
                                    size={26}
                                    css={css`
                                        margin-right: 0;
                                        position:relative;
                                        top:1px;
                                    `}
                                    style={{ fill: "#ed8c07" }}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.incoMessageWrapper}>
                                {ldIncoVisibility && cart && stCountry !== "" && stCountry !== "US" && siteName !== "TestEquity UK" && (
                                    <>
                                        <Typography {...styles.incoTermUrlMessage}>{translate("INCO Message")}&nbsp;</Typography>
                                        <a href={incoTermUrl?.incoTermUrl}>
                                            <Typography {...styles.incoTermLinkTypography}>{translate("here")}</Typography>
                                        </a>
                                    </>
                                )}
                                {ldDryiceMessageVisibility && dryIceMessage && hasMD && (
                                    <Typography {...styles.incoTermUrlMessage}>{dryIceMessage}</Typography>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                )
            }
            {
                (!(ldIncoVisibility && (cart && stCountry !== "" && stCountry !== "US" && siteName !== "TestEquity UK"))) && (
                    <>
                        {/* This block and 'red' versions of styles are specifically for launch darkly false state */}
                        <StyledWrapper {...styles.incoMessageWrapperRed}>
                            <Typography {...styles.incoTermUrlMessageRed}>{translate("INCO Message")}&nbsp;</Typography>
                            <a href={incoTermUrl?.incoTermUrl}>
                                <Typography {...styles.incoTermLinkTypographyRed}>{translate("here")}</Typography>
                            </a>
                        </StyledWrapper>
                        {/* */}
                    </>
                )
            }
            {hasMG && (
                <GridItem {...styles.crsHeaderHazmatMessageGridItem}>
                    <Typography as="div" {...styles.crsHeaderHazmatMessage}>
                        <p><b>Warning:</b> This order contains hazardous materials. Please see individual line items below for more information.</p>
                    </Typography>
                </GridItem>
            )}
            {hasML && (
                <GridItem {...styles.crsHeaderShipFreightGridItem}>
                    <Typography as="div" {...styles.crsHeaderShipFreightMessage}>
                        <p><b>Important:</b> An item in this order must be shipped as freight. Please see individual line items below for more information.</p>
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(CheckoutReviewAndSubmitHeader)),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Page Header",
    },
};

export default widgetModule;
